import scrollama from "scrollama";
import { useEffect, useState } from "react";
import ResponsiveImage from "../../atoms/image/ResponsiveImage";
import CTA from "../../organisms/common/CTA";
import VideoHighlight from "./VideoHighlight";
import {
  viewport,
  colors,
  font,
  lineHeight,
} from "../../../styles/cssinjs/helpers/variables.js";
import { replaceHrefInAnswer } from "./SignupUrlReplacer";

const P = ({ children }) => {
  return <p className="my-2" dangerouslySetInnerHTML={{ __html: children }} />;
};
const H2 = ({ children, isNewDesign }) => {
  let isFoundEmoji = false;
  if (children.includes("💡")) {
    isFoundEmoji = true;
  }
  let lastChar = children.charAt(children.length - 1);

  return (
    <h2
      className={`title lg:mb-3 md:d-block ff-euclid text-md fw-700 ${
        isNewDesign === true ? "md:mt-5 md:mb-3" : "md:mb-2 md:d-none"
      }`}
    >
      {children}
      {isFoundEmoji === true ? " " : lastChar !== "?" && lastChar !== "!" && ""}
      <style jsx>{`
        ${isNewDesign
          ? `
          .title {
            font-size: ${font.size.lg[2.5]};
            line-height: ${lineHeight[12]};
          }
          @media (max-width: ${viewport.tablet.sm}) {
            .title {
              font-size: ${font.size.lg[1]};
              line-height: ${lineHeight[10]};
            }
          }`
          : `
          .title {
            font-size: ${font.size.lg[1]};
            line-height: ${lineHeight[10]};
          }
        `}
      `}</style>
    </h2>
  );
};

const HeadingWithIcon = ({ data }) => {
  return (
    <div
      className={`fx fx--cv  ${
        data.hasOwnProperty("iconLarge")
          ? "lg:mt-5 md:mt-5 lg:mb-3 md:mb-3"
          : data.hasOwnProperty("iconSmall")
          ? "lg:mt-3 md:mt-3 icon-wrap"
          : ""
      }`}
    >
      {/* width 80px and height 80px */}
      {data.hasOwnProperty("iconLarge") && (
        <figure
          className="company-icon icon-large"
          dangerouslySetInnerHTML={{ __html: data.iconLarge }}
        />
      )}
      {/* width 40px and height 40px but for tablet-sm width 48px and height 48px */}
      {data.hasOwnProperty("iconSmall") && (
        <figure
          className="company-icon icon-small"
          dangerouslySetInnerHTML={{ __html: data.iconSmall }}
        />
      )}
      {/* width 40px and height 40px */}
      {data.hasOwnProperty("icon") && (
        <figure
          className="brand-icon mr-2"
          dangerouslySetInnerHTML={{ __html: data.icon }}
        />
      )}
      <h2
        className="icon-heading"
        dangerouslySetInnerHTML={{ __html: data.heading }}
      />
      <style global jsx>{`
        .icon-heading {
          position: relative;
        }
        .icon-large + .icon-heading {
          font-size: ${font.size.md[3]};
          line-height: ${lineHeight[8]};
          padding-left: 24px;
        }
        .icon-small + .icon-heading {
          font-size: ${font.size.md[1]};
          line-height: ${lineHeight[6]};
          padding-left: 16px;
        }
        .icon-wrap + p {
          margin: 8px 0px !important;
        }
        .brand-card {
          padding: 24px 24px 32px;
          border-radius: 8px;
          box-shadow: 0 1px 2px 0 rgba(48, 58, 71, 0.2),
            0 1px 3px 0 rgba(48, 58, 71, 0.1);
          background-color: ${colors.primitive.white.default};
        }
        .brand-icon {
          width: 40px;
          height: 40px;
        }
        .brand-icon + .icon-heading {
          font-size: ${font.size.md[2]};
          line-height: ${lineHeight[7]};
        }
        @media (min-width: ${viewport.tablet.sm}) {
          .company-icon.icon-large img {
            width: 80px;
            height: 80px;
          }
          .company-icon.icon-small img {
            width: 40px;
            height: 40px;
          }
        }
        @media (max-width: ${viewport.tablet.sm}) {
          .icon-large + .icon-heading {
            font-size: ${font.size.md[2]} !important;
            line-height: ${lineHeight[7]} !important;
          }
          .company-icon.icon-large img {
            width: 48px !important;
            height: 48px !important;
          }
        }
      `}</style>
    </div>
  );
};

const SubHeading = ({ data, isNewDesign }) => {
  let headingClassName = "";
  let headingKey = "";
  // classnames can differ based on the heading key

  // heading key
  // laptop and tablet => font-size 32px , line height 40px
  // mobile => font-size 24px , line-height 32px
  if (data.hasOwnProperty("heading")) {
    headingClassName = `lg:mt-5 lg:mb-3 md:mb-2 sub-heading ${
      isNewDesign === true ? "md:mt-5" : ""
    }`;
    headingKey = "heading";
  }
  // titleHeading key
  // laptop and tablet => font-size 40px , line height 48px
  // mobile => font-size 32px , line-height 40px
  if (data.hasOwnProperty("titleHeading")) {
    headingClassName = "lg:mb-3 md:mt-5 md:mb-2 title-heading";
    headingKey = "titleHeading";
  }
  // subTitleHeading key
  // laptop and tablet => font-size 32px , line height 40px
  // mobile => font-size 24px , line-height 32px
  if (data.hasOwnProperty("subTitleHeading")) {
    headingClassName = "mt-5 mb-3 sub-title-heading";
    headingKey = "subTitleHeading";
  }
  // childHeading key
  // laptop and tablet => font-size 24px , line height 32px
  // mobile => font-size 20px , line-height 28px
  if (data.hasOwnProperty("childHeading")) {
    headingClassName = "mt-3 mb-3 text-mdx";
    headingKey = "childHeading";
  }
  // headingWithoutIcon key
  // laptop ,tablet and mobile => font-size 24px , line height 32px
  if (data.hasOwnProperty("headingWithoutIcon")) {
    headingClassName = "lg:mt-5 lg:mb-3 md:mt-5 md:mb-2 heading-wot-icon";
    headingKey = "headingWithoutIcon";
  }
  return (
    <>
      <h2
        {...(data.id ? { id: data.id } : {})}
        className={`${headingClassName}`}
        dangerouslySetInnerHTML={{ __html: data[`${headingKey}`] }}
      />
      <style global jsx>{`
        .sub-heading,
        .sub-title-heading,
        .sub-heading .list {
          font-size: ${font.size.lg[1]};
          line-height: ${lineHeight[10]};
        }
        .title-heading {
          font-size: ${font.size.lg[2.5]};
          line-height: ${lineHeight[12]};
        }
        .heading-wot-icon {
          font-size: ${font.size.md[3]};
          line-height: ${lineHeight[8]};
        }
        .sub-heading {
          padding-left: 32px;
          position: relative;
        }
        .sub-heading .list {
          display: inline-block;
          margin-right: 18px;
          text-align: center;
          position: absolute;
          top: 0px;
          left: 0;
        }
        @media (max-width: ${viewport.tablet.sm}) {
          .sub-heading,
          .sub-title-heading,
          .sub-heading .list {
            font-size: ${font.size.md[3]};
            line-height: ${lineHeight[8]};
          }
          .title-heading {
            font-size: ${font.size.lg[1]};
            line-height: ${lineHeight[10]};
          }
        }
      `}</style>
    </>
  );
};

const H3 = ({ children, isNewDesign }) => {
  return (
    <>
      <h3
        className={`${
          isNewDesign ? "child-heading mt-5" : "lg:mb-2 md:mb-1 lg:mt-6"
        }`}
      >
        {children}
      </h3>
      <style jsx>{`
        h3 {
          font-size: ${font.size.md[2]}!important;
          line-height: ${lineHeight[7]};
        }
      `}</style>
    </>
  );
};
export const PlanTable = ({ data }) => {
  return (
    <table className="plan-table my-5">
      <thead>
        <tr>
          {data.labels.map((item, index) => {
            return <th key={index}>{item}</th>;
          })}
        </tr>
      </thead>
      <tbody>
        {data.list &&
          data.list?.map((item, rowIndex) => (
            <tr key={rowIndex}>
              {item.map((data, cellIndex) => (
                <td key={cellIndex}>
                  <p dangerouslySetInnerHTML={{ __html: data }} />
                </td>
              ))}
            </tr>
          ))}
        {data.listWithBullets &&
          data.listWithBullets.map(
            ([link, { text, ul, additionalText }], rowIndex) => (
              <tr key={rowIndex}>
                <td>
                  <p dangerouslySetInnerHTML={{ __html: link }} />
                </td>
                <td>
                  <div className="table-new-design">
                    {text && <p dangerouslySetInnerHTML={{ __html: text }} />}
                    {ul && <Ul data={ul} isNewDesign={true} />}
                    {additionalText && (
                      <p
                        dangerouslySetInnerHTML={{
                          __html: additionalText,
                        }}
                      />
                    )}
                  </div>
                </td>
              </tr>
            )
          )}
      </tbody>
      <style jsx>{`
        .plan-table,
        th,
        td {
          border: 1px solid ${colors.primitive.white[200]};
          border-collapse: collapse;
          text-align: center;
        }
        .plan-table {
          font-size: ${font.size.sm[4]};
          line-height: ${lineHeight[12]};
        }
        .plan-table th {
          padding: 16px;
          color: ${colors.primitive.grey[300]};
          line-height: ${lineHeight[6]};
        }
        .plan-table th:nth-child(1) {
          width: 45%;
        }
        .plan-table td {
          padding: 16px;
          color: ${colors.primitive.grey[800]};
          background-color: ${colors.primitive.white.default};
          vertical-align: middle;
        }
        .plan-table tr td p:nth-child(1) {
          font-size: ${font.size.sm[4]};
          line-height: ${lineHeight[6]};
        }
        .plan-table tr td:nth-child(2) p {
          font-size: ${font.size.sm[3]};
          line-height: ${lineHeight[6]};
          text-align: left;
        }
      `}</style>
    </table>
  );
};

const H4 = ({ children }) => {
  return (
    <h4 className="lg:mb-2 md:mb-1 lg:mt-6">
      {children}
      <style jsx>{`
        h4 {
          font-size: ${font.size.md[1]}!important;
          line-height: ${lineHeight[7]};
        }
      `}</style>
    </h4>
  );
};
const Button = ({ data, ...props }) => {
  return (
    <a href={data.href} className={`btn-${data.type} my-3`} {...props}>
      <span>{data.text}</span>
    </a>
  );
};
const EmbedImage = ({ data }) => {
  return (
    <figure className="mt-5 mb-5" dangerouslySetInnerHTML={{ __html: data }} />
  );
};
const AddID = ({ data }) => {
  return <div id={data}>&nbsp;</div>;
};

const VideoHighlightSection = ({
  data,
  toggleVideo,
  isVideoModalVisible,
  videoId,
}) => {
  return (
    <section className="container lg:maxw-xl lg:mb-20 md:mb-10 md:-mt-6">
      <VideoHighlight
        data={data}
        toggleVideo={(id) => {
          toggleVideo(id);
        }}
        isVideoModalVisible={isVideoModalVisible}
        videoId={videoId}
        className="lg:w-auto"
      />
    </section>
  );
};

const Card = ({ data, isNewDesign }) => {
  let hasTitle = data.hasOwnProperty("title");
  return (
    <div
      className={`faq lg:ml-0 lg:mb-5 lg:px-3 lg:pt-3 lg:pb-4 md:fx--w md:pt-2.5 md:pl-2.5 md:pr-2.5 md:pb-2.5 md:mb-2.5 ${
        hasTitle ? `` : `fx`
      } ${
        isNewDesign === true
          ? "text-mdl mb-1 bg-dull-grey mt-10"
          : "bg-white mt-5 md:mt-2.5"
      }`}
    >
      {data.hasOwnProperty("icon") && (
        <figure dangerouslySetInnerHTML={{ __html: data.icon }} />
      )}
      {hasTitle && (
        <h2
          className={`ff-euclid fw-600 text-brandcolor ${
            isNewDesign === true ? "text-mdl mb-1" : ""
          }`}
        >
          {data.title}
        </h2>
      )}

      <div className={`${hasTitle ? `` : `lg:pl-3`} md:mt-1`}>
        {typeof data.desc === "object" ? (
          data.desc.map((item, index) => {
            return (
              <p
                key={index}
                className={`text-smx ${index === 0 ? "" : "mt-1"}`}
                dangerouslySetInnerHTML={{ __html: item }}
              />
            );
          })
        ) : (
          <p
            className={`${isNewDesign === true ? "" : "text-smx"}`}
            dangerouslySetInnerHTML={{ __html: data.desc }}
          />
        )}
      </div>
      <style jsx>{`
        ${isNewDesign
          ? `
         .faq h2 {
            color: ${colors.primitive.grey[800]} !important;
          }
          `
          : ``}
      `}</style>
    </div>
  );
};
const OutlineCards = ({ data }) => {
  return data.map((item, index) => {
    return (
      <a
        key={index}
        href={item.href}
        className="card-step text-brandcolor lg:mb-2 md:mb-1"
      >
        {item.text}
        <style jsx>{`
          .card-step {
            display: inline-block;
            width: 100%;
            padding: 20px 50px 24px 32px;
            border-radius: 8px;
            border: 1.5px solid ${colors.primitive.black[300]};
            font-size: ${font.size.md[1]};
            font-weight: ${font.weight.bold};
          }
          .card-step:hover {
            background-color: ${colors.primitive.white.default};
            box-shadow: 0 14px 32px 0 rgba(22, 45, 61, 0.08),
              0 1px 4px 0 rgba(22, 45, 61, 0.1);
            border-color: ${colors.primitive.white.default};
          }
          @media (max-width: ${viewport.tablet.sm}) {
            .card-step {
              padding: 20px 18px 24px 20px;
            }
          }
        `}</style>
      </a>
    );
  });
};
const DownloadCard = ({ data }) => {
  return (
    <div className="faq lg:maxw-smd lg:ml-0 lg:mb-0 lg:px-3 lg:pt-3 lg:pb-4 md:pt-2.5 md:pl-2.5 md:pr-2.5 md:pb-2.5 md:mt-2.5 md:mb-2.5 bg-white">
      <h4
        className="text-md lg:mb-3 md:mb-1.5 text-grey"
        dangerouslySetInnerHTML={{ __html: data.title }}
      />
      <div className="fx fx--sb">
        {data.btns.map((item, index) => {
          return (
            <a key={index} href={item.href} className="btn-appstore">
              <figure>
                <img src={item.src} alt={item.alt} loading="lazy" />
              </figure>
            </a>
          );
        })}
      </div>
    </div>
  );
};
const Ul = ({ data, isNewDesign }) => {
  return (
    <ul className="unordered-bulletin text-sm ml-2 my-2">
      {data.map((item, index) => {
        return <li key={index} dangerouslySetInnerHTML={{ __html: item }} />;
      })}
      <style jsx>{`
        .unordered-bulletin li {
          padding-left: 11px;
          position: relative;
          margin-bottom: 16px;
        }
        .unordered-bulletin li:before {
          content: "";
          display: inline-block;
          width: 7px;
          height: 7px;
          background: ${colors.primitive.black[500]};
          margin-right: 18px;
          border-radius: 50%;
          border: 0;
          line-height: normal;
          text-align: center;
          position: absolute;
          top: 11px;
          left: -7px;
        }
        ${isNewDesign
          ? `
          .unordered-bulletin li {
            margin-bottom: 0px !important;
          }
          `
          : ``}
      `}</style>
    </ul>
  );
};
const Ol = ({ data }) => {
  return (
    <ol className="ordered-bulletin text-sm md:ml-2 my-2">
      {data.map((item, index) => {
        return (
          <li
            key={index}
            data-id={index + 1}
            dangerouslySetInnerHTML={{ __html: replaceHrefInAnswer(item) }}
          />
        );
      })}
      <style jsx>{`
        .ordered-bulletin li {
          padding-left: 40px;
          position: relative;
        }
        .ordered-bulletin li:before {
          content: attr(data-id);
          display: inline-block;
          width: 20px;
          height: 20px;
          margin-right: 18px;
          font-size: ${font.size.sm[2]};
          font-weight: ${font.weight.semiBold};
          line-height: ${lineHeight[5]};
          border: 2px solid;
          border-radius: 50%;
          text-align: center;
          position: absolute;
          top: 5px;
          left: 0;
        }
      `}</style>
    </ol>
  );
};
export const PrimaryCta = ({ data }) => {
  const appMode = process.env.APP_MODE;
  const link = data?.btn?.href?.[appMode] || data?.btn?.href;
  return (
    <div className="faq lg:px-3 lg:pt-3 lg:pb-4 md:pt-2.5 md:pl-2.5 md:pr-2.5 md:pb-2.5 lg:mt-5 lg:mb-5 md:mt-2.5 md:mb-2.5 bg-blue">
      <h3 className="lg:mb-3 md:mb-5 text-white ff-euclid">{data.title}</h3>
      {data.hasOwnProperty("btn") && (
        <a
          href={link}
          className="btn-secondary bg-white btn-lg md:w-100 md:px-4"
        >
          {data.btn.text}
        </a>
      )}
      {data.hasOwnProperty("desc") && typeof data.desc === "object" ? (
        data.desc.map((item, index) => {
          return (
            <p
              key={index}
              className={`text-white ${index === 0 ? "" : "mt-1"}`}
              dangerouslySetInnerHTML={{ __html: item }}
            />
          );
        })
      ) : (
        <p
          className="text-white"
          dangerouslySetInnerHTML={{ __html: data.desc }}
        />
      )}
    </div>
  );
};
const Blockquote = ({ data }) => {
  return (
    <div className="lg:mt-5 lg:mb-5 md:mt-2 md:mb-2 ">
      <div className="lg:maxw-mds lg:d-fx">
        <span className="apostrophe">“</span>
        {typeof data.text === "object" ? (
          <blockquote className="lg:ml-3 text-sm fs-italic md:-mt-4.5">
            {data.text.map((item, index) => {
              return (
                <p
                  key={index}
                  className={`${index === 0 ? `` : `mt-1`}`}
                  dangerouslySetInnerHTML={{ __html: item }}
                />
              );
            })}
          </blockquote>
        ) : (
          <blockquote
            className="lg:ml-3 text-sm fs-italic md:-mt-4.5"
            dangerouslySetInnerHTML={{ __html: data.text }}
          />
        )}
      </div>
      <cite className="d-block text-sm lg:mb-5 lg:ml-7 mt-1 md:mb-3">
        {data.author}
      </cite>
      <style jsx>{`
        .apostrophe {
          font-size: ${font.size.xl[2]};
          font-family: Georgia, "Times New Roman", Times, serif;
          color: ${colors.derived.brand};
          margin-left: -2px;
        }
        .apostrophe + blockquote.fs-italic {
          color: ${colors.primitive.grey[300]};
        }
      `}</style>
    </div>
  );
};
const StatsText = ({ data }) => {
  return (
    <div className="lg:mt-4 lg:mb-4 md:mt-2 md:mb-2">
      <p className="lg:maxw-smd lg:mt-2 md:mt-2 mb-1">{data.text}</p>
      <span className="avg-pay">{data.number}</span>
    </div>
  );
};
const ImgCaption = ({ data }) => {
  return data.imgCaption?.map((item, index) => {
    return (
      <>
        {item !== undefined ? (
          <span
            key={index}
            className="img-caption-text"
            dangerouslySetInnerHTML={{ __html: item }}
          />
        ) : (
          ""
        )}
        <style jsx>{`
          .img-caption-text {
            position: relative;
            top: -24px;
            display: block !important;
            font-size: ${font.size.sm[4]};
            line-height: ${lineHeight[7]};
            color: ${colors.primitive.grey[300]};
          }
        `}</style>
      </>
    );
  });
};
const NoteText = ({ children }) => {
  return (
    <>
      <p
        id="note"
        className="my-4"
        dangerouslySetInnerHTML={{ __html: children }}
      />
      <style jsx>{`
        #note {
          font-size: ${font.size.sm[4]};
          line-height: ${lineHeight[7]};
          color: ${colors.primitive.grey[300]};
        }
      `}</style>
    </>
  );
};
const StatsTextWithHeading = ({ data }) => {
  return (
    <div className="lg:mt-4  lg:mb-4 md:mt-2.5 md:mb-2.5">
      <h3 className="ff-euclid fw-600 text-brandcolor">{data.number}</h3>
      <p className="mt-1" dangerouslySetInnerHTML={{ __html: data.text }} />
    </div>
  );
};

const Icons = ({ data }) => {
  return (
    <div className="fx fx--sb lg:mx-7 md:ml-2 md:mr-2">
      {data.icons.map((item, index) => {
        return (
          <figure key={index}>
            <img
              className="w-auto"
              loading="lazy"
              src={item.src}
              alt={item.alt}
              width="48"
              height="49"
            />
          </figure>
        );
      })}
    </div>
  );
};

const PricingComparison = ({ data }) => {
  return (
    <div className="mt-5">
      <Icons data={data} />
      {data.features.map((item, index) => {
        return (
          <>
            <div className="bg-dull-grey br-8 fx fx--sb lg:mb-2.5 lg:mt-2.5 lg:py-2 md:mb-2.5 md:mt-2.5 md:py-2 w-auto">
              <span className="mx-auto">{item.title}</span>
            </div>
            <ul className="ticked-bulletin list-item square-list fx fx--sb mx-3">
              {item.availability.map((item, index) => {
                if (item === "yes") {
                  return <li key={index}></li>;
                }
                if (item === "no") {
                  return (
                    <li key={index} className="none">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="18"
                        height="18"
                        fill="none"
                        viewBox="0 0 18 18"
                      >
                        {" "}
                        <path
                          stroke="#3A4D66"
                          strokeLinecap="round"
                          strokeWidth="1.5"
                          d="M17 1.243l-16 16M17 17.243l-16-16"
                        ></path>{" "}
                      </svg>
                      <style jsx>{`
                        .none:before {
                          background: none;
                        }
                      `}</style>
                    </li>
                  );
                }
                return (
                  <li
                    key={index}
                    dangerouslySetInnerHTML={{ __html: item }}
                    className="none"
                  />
                );
              })}
            </ul>
          </>
        );
      })}
    </div>
  );
};
const MobileInput = ({ id }) => {
  return (
    <input
      className="radio d-none pos-abs pos-abs--l-0 pos-abs--b-0"
      type="checkbox"
      name="faq-input"
      id={`chck${id}`}
    />
  );
};
const MobileLabel = ({ children, htmlFor, isNewDesign }) => {
  return (
    <label
      className={`faq-section-header md:d-block ff-euclid text-md fw-700 text-grey lg:d-none md:mb-2.5 md:pr-3 ${
        isNewDesign === true ? "md:d-none" : ""
      }`}
      htmlFor={`chck${htmlFor}`}
    >
      {children}
    </label>
  );
};
const SubNav = ({ data }) => {
  return (
    <li className="lg:mb-2">
      <a href={data.href} className="nav-high ff-euclid text-md">
        {data.text}
      </a>
    </li>
  );
};
export const NavList = ({ data, isNewDesign }) => {
  return (
    <nav
      className={`fx--g-1 lg:maxw-xss ${isNewDesign === true ? "lg:mr-3" : ""}`}
      id="sidenav"
    >
      <ul className="faq-navigation is-sticky">
        {Array.isArray(data) &&
          data.map((item, index) => {
            if (item.hasOwnProperty("title")) {
              return item.hasOwnProperty("subNav") ? (
                <li
                  key={index}
                  className={index == data.length - 1 ? `` : `lg:mb-2 `}
                >
                  <a href={`#${item.id}`} className="faq-nav-link ff-euclid">
                    {item.title}
                  </a>
                  <ol className="lg:mt-2" id="sub-faqs">
                    {item.subNav.map((subitem, index) => {
                      return <SubNav key={index} data={subitem} />;
                    })}
                  </ol>
                </li>
              ) : (
                <li
                  key={index}
                  className={index == data.length - 1 ? `` : `lg:mb-2 `}
                >
                  <a href={`#${item.id}`} className="faq-nav-link ff-euclid">
                    {item.title}
                  </a>
                </li>
              );
            }
          })}
      </ul>
      <style jsx>{`
        a {
          font-size: ${font.size.sm[4]};
          line-height: ${lineHeight[6]};
        }
        ${isNewDesign
          ? `
          .is-sticky {
            position: sticky;
            top: 160px;
            height: calc(100vh - 230px);
            overflow: auto;
          }
          .faq-navigation li {
            border-bottom: 1.5px solid ${colors.primitive.white.default} !important;
            padding-bottom: 16px !important;
          }
          .faq-navigation li:last-child {
            border-bottom: none !important;
          }
          `
          : `
          .is-sticky {
          position: sticky;
          top: 100px;
          height: calc(100vh - 100px);
          overflow: auto;
        }
        `}
        @media (max-width: ${viewport.tablet.md}) {
          nav {
            display: none;
          }
        }
      `}</style>
    </nav>
  );
};

export const ContentGenerator = ({ data, isNewDesign }) => {
  const [isVideoModalVisible, setVideoModalVisible] = useState(false);
  const [videoId, setVideoId] = useState(null);
  const toggleVideo = (id) => {
    setVideoModalVisible(!isVideoModalVisible);
    setVideoId(id);
  };
  return (
    <>
      {data.content?.map((item, index) => {
        if (item.hasOwnProperty("text")) {
          if (typeof item.text === "string") {
            return <p key={index}>{item.text}</p>;
          } else if (Array.isArray(item.text)) {
            return item.text.map((text, index) => {
              return <P key={index}>{text}</P>;
            });
          }
        }
        // For Text Content
        if (item.hasOwnProperty("contentWithURL")) {
          return (
            <div className="mb-3 word-break" key={index}>
              {item.contentWithURL.map((item, index) => {
                return <P key={index}>{item}</P>;
              })}
            </div>
          );
        }
        // For Heading
        if (item.hasOwnProperty("title")) {
          return (
            <H2 key={index} isNewDesign={isNewDesign}>
              {item.title}
            </H2>
          );
        }

        // For Sub Heading - New Design
        if (item.hasOwnProperty("subHeading")) {
          return (
            <SubHeading
              key={index}
              data={item.subHeading}
              isNewDesign={isNewDesign}
            />
          );
        }

        if (item.hasOwnProperty("secondaryCta")) {
          return (
            <CTA
              key={index}
              data={item.secondaryCta}
              isNewDesign={isNewDesign}
              imgClassName="img-fit"
              className="bg-light-blue-grey cta-section lg:mt-10 lg:pb-10"
              colorScheme="white-grey-black"
            />
          );
        }
        // For Heading With Icon - New Design
        if (item.hasOwnProperty("headingWithIcon")) {
          return <HeadingWithIcon key={index} data={item.headingWithIcon} />;
        }
        // For Heading Icon with description - New Design
        if (item.hasOwnProperty("headingWithBoxShadow")) {
          return (
            <div className="brand-card mt-5 mb-5" key={index}>
              <HeadingWithIcon key={index} data={item.headingWithBoxShadow} />
              <p
                className="mt-2"
                dangerouslySetInnerHTML={{
                  __html: item.headingWithBoxShadow.detail,
                }}
              />
            </div>
          );
        }

        // For Heading Level 3
        if (item.hasOwnProperty("heading3")) {
          return (
            <H3 key={index} isNewDesign={isNewDesign}>
              {item.heading3}
            </H3>
          );
        }

        // For Heading Level 4
        if (item.hasOwnProperty("heading4")) {
          return <H4 key={index}>{item.heading4}</H4>;
        }

        // For Note Text
        if (item.hasOwnProperty("note")) {
          return <NoteText key={index}>{item.note}</NoteText>;
        }
        // For UnorderedList
        if (item.hasOwnProperty("ul")) {
          return <Ul key={index} data={item.ul} isNewDesign={isNewDesign} />;
        }

        // For OrderedList
        if (item.hasOwnProperty("ol")) {
          return <Ol key={index} data={item.ol} />;
        }

        // for tables
        if (item.hasOwnProperty("planTable")) {
          return <PlanTable key={index} data={item.planTable} />;
        }

        //For span text
        if (item.hasOwnProperty("imgCaption")) {
          return <ImgCaption key={index} data={item} />;
        }

        // For Images
        if (item.hasOwnProperty("image")) {
          return (
            <ResponsiveImage
              key={index}
              loading="lazy"
              alt={item.image.alt}
              src={item.image.src}
              width={item.image.dimensions[0]}
              height={item.image.dimensions[1]}
              dpi={item.image.prefixes.dpi}
              mobile={item.image.prefixes.mobile}
              hasMobileImg={
                item.image.prefixes.hasOwnProperty("mobile")
                  ? item.image.prefixes.mobile
                  : false
              }
              wrapClass={`${
                isNewDesign === true
                  ? "article-image"
                  : "lg:mt-3 lg:mb-5 md:mt-2.5 md:mb-2.5"
              }`}
              imgClass={
                item.image.hasOwnProperty("className")
                  ? item.image.className
                  : "img-fit"
              }
            />
          );
        }

        // For Embedded Image
        if (item.hasOwnProperty("embedImage")) {
          return <EmbedImage key={index} data={item.embedImage} />;
        }
        // For Card
        if (item.hasOwnProperty("card")) {
          return (
            <Card key={index} data={item.card} isNewDesign={isNewDesign} />
          );
        }
        //For Video
        if (item.hasOwnProperty("videos")) {
          return (
            <VideoHighlightSection
              key={index}
              data={item.videos}
              toggleVideo={(id) => {
                toggleVideo(id);
              }}
              isVideoModalVisible={isVideoModalVisible}
              videoId={videoId}
            />
          );
        }
        // For Button
        if (item.hasOwnProperty("button")) {
          return <Button key={index} data={item.button} />;
        }

        // For Outline Card
        if (item.hasOwnProperty("outlineCards")) {
          return <OutlineCards key={index} data={item.outlineCards} />;
        }

        // For Download Card
        if (item.hasOwnProperty("downloadCard")) {
          return <DownloadCard key={index} data={item.downloadCard} />;
        }

        // For CTA
        if (item.hasOwnProperty("cta")) {
          return <PrimaryCta key={index} data={item.cta} />;
        }
        // For Blockquote
        if (item.hasOwnProperty("blockquote")) {
          return <Blockquote key={index} data={item.blockquote} />;
        }

        // For Stats Text
        if (item.hasOwnProperty("stats")) {
          return <StatsText key={index} data={item.stats} />;
        }

        // For Stats Text With Heading
        if (item.hasOwnProperty("statsWithHeading")) {
          return (
            <StatsTextWithHeading key={index} data={item.statsWithHeading} />
          );
        }

        // For Stats Text With Heading
        if (item.hasOwnProperty("pricingComparison")) {
          return (
            <PricingComparison key={index} data={item.pricingComparison} />
          );
        }

        // For Subnavs
        if (item.hasOwnProperty("id")) {
          return <AddID key={index} data={item.id} />;
        }
      })}
    </>
  );
};

const handleStepEnter = (response) => {
  const { element } = response;
  // Check if the section is entering the viewport
  const targetElement = element.dataset.id;
  // remove active navs
  let navs = document.querySelectorAll("#sidenav .faq-nav-link");

  function removeActive() {
    navs.forEach((nav) => {
      nav.classList.remove("selected");
    });
  }

  removeActive();

  if (targetElement !== undefined) {
    document
      .querySelector(`[href="#${targetElement}"]`)
      .classList.add("selected");
  } else {
    removeActive();
  }
};

export const SideContent = ({ data, isNewDesign }) => {
  return (
    <>
      {Array.isArray(data) &&
        data.map((item, index) => {
          return (
            <article
              className={`${
                isNewDesign === true ? "pt-10" : "lg:mb-15 md:mb-2"
              }`}
              id={item.id}
              key={index}
              data-id={item.id}
            >
              <MobileInput id={index + 1} />
              <MobileLabel htmlFor={index + 1} isNewDesign={isNewDesign}>
                {item.title}
              </MobileLabel>
              <div className="faq-section">
                <div
                  className={`${
                    isNewDesign === true ? "" : "lg:mb-5 md:mt-2.5"
                  }`}
                >
                  <ContentGenerator
                    data={item}
                    key={index}
                    isNewDesign={isNewDesign}
                  />
                </div>
              </div>
              <style jsx>{`
                article:before {
                  display: none !important;
                }
                ${isNewDesign
                  ? `
                  .faq-section .article-image{
                    margin-top: 40px;
                    margin-bottom: 40px;
                  }
                  .faq-section .faq{
                    border: solid 4px ${colors.primitive.white.default};
                  }
                  @media (min-width: ${viewport.desktop.sm}) {
                    .faq-section > * >:last-child {
                      margin-bottom: 0px!important;
                    }
                    .faq-section .article-image:first-child{
                      margin-top: 0px;
                    }
                  }
                  @media (max-width: ${viewport.desktop.sm}) {
                    .faq-section > * >:first-child {
                      margin-top: 0px!important;
                    }
                    .faq-section > * >:last-child {
                      margin-bottom: 0px!important;
                    }
                  } `
                  : ``}
              `}</style>
            </article>
          );
        })}
    </>
  );
};

export default function SideNavHighlight({ data, isNewDesign }) {
  useEffect(() => {
    // initialize the scrollama
    const scroller = scrollama();
    scroller
      .setup({
        step: "#faq-id > article", // Replace with your content section selector
        offset: 0.3, // Adjust this value as needed (0.5 means 50% of the section must be visible)
      })
      .onStepEnter(handleStepEnter);
  }, []);

  return (
    <>
      <NavList data={data} isNewDesign={isNewDesign} />
      <div
        className={`faq-content ${
          isNewDesign === true ? "lg:maxw-lg lg:ml-12.5" : "lg:maxw-xl lg:mt-5"
        }`}
        id="faq-id"
      >
        <SideContent data={data} isNewDesign={isNewDesign} />
      </div>
      <style jsx>{`
        @media (min-width: ${viewport.desktop.sm}) {
          .faq-content :global(img) {
            height: auto;
          }
        }
        @media (min-width: ${viewport.tablet.sm}) and (max-width: ${viewport
            .tablet.md}) {
          .faq-content {
            margin-left: auto !important;
            margin-right: auto;
            max-width: 600px;
          }
        }
        ${isNewDesign
          ? `
          @media (min-width: ${viewport.desktop.sm}) {
            .faq-content :global(img) {
              width: auto;
              height: auto;
            }
            .faq-content > article:first-child {
              padding-top: 0px!important;
              margin-top: -16px;
            }
          }
           @media (max-width: ${viewport.tablet.sm}) {
            .faq-content img {
              width: 100%;
              height: auto;
            }
            .faq-content > article:first-child {
              padding-top: 40px!important;
            }
           }  
          `
          : ``}
      `}</style>
    </>
  );
}
